import { getByID } from '@adornis/baseql/operations/mongo.js';
import { BuchungsheldLitElement } from '@adornis/buchungsheld-components/BuchungsheldLitElement.js';
import { Institution } from '@adornis/buchungsheld-db/entities/institution.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-empty-placeholder.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-section.js';
import { DesignSystem } from '@adornis/chemistry/elements/theming/design.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import '@adornis/file-utils/client/x-image.js';
import '@adornis/forms/x-checkbox.js';
import '@adornis/forms/x-input.js';
import { useObservable } from '@adornis/functional-lit/stateful.js';
import { makeRouter } from '@adornis/router/client/make-router.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { clientTranslator } from '@adornis/translation-core/client/clientTranslator.js';
import { watch } from '@lit-labs/preact-signals';
import { html } from 'lit';
import { customElement } from 'lit/decorators.js';
import { from, of } from 'rxjs';
import '../../../../layout/bh-icon-link.js';
import './bh-page-admin-einrichtung-settings-accessibility.js';
import './bh-page-admin-einrichtung-settings-certificates.js';
import './bh-page-admin-einrichtung-settings-colors.js';
import './bh-page-admin-einrichtung-settings-contracts';
import './bh-page-admin-einrichtung-settings-embedform.js';
import './bh-page-admin-einrichtung-settings-general-info.js';
import './bh-page-admin-einrichtung-settings-imprint.js';
import './bh-page-admin-einrichtung-settings-mails.js';
import './bh-page-admin-einrichtung-settings-privacy.js';
import './bh-page-admin-einrichtung-settings-product-page.js';
import './bh-page-admin-einrichtung-settings-terms.js';

export const adminEinrichtungSettingsRouter = makeRouter({
  root: {
    path: '/',
    render: params => {
      const design = useObservable(() => DesignSystem.currentTheme, []);
      const institution = useObservable(
        () => (params.institutionID ? from(getByID(Institution, params.institutionID)({})) : of(null)),
        [params.institutionID],
      );

      return html`
        <x-flex space="xl">
          <bh-breadcrumb
            .items=${[{ content: clientTranslator.translate('settings', 'name'), active: true }]}
            .additionalAction=${html`
              <tr-button-small
                .color=${design?.colors.tone.error}
                icon="delete"
                .action=${async () => {
                  if (!institution) return;
                  if (await XDialog.confirm(clientTranslator.translate('settings', 'confirm_del'))) {
                    institution.remove();
                    goTo('/admin');
                  }
                }}
              ></tr-button-small>
            `}
          ></bh-breadcrumb>
          <x-flex padding="md" space="xs">
            <bh-icon-link
              icon="date_range"
              text=${clientTranslator.translate('settings', 'general_information')}
              .link=${adminEinrichtungSettingsRouter.link('general-info')}
            ></bh-icon-link>
            <bh-icon-link
              icon="approval"
              text=${clientTranslator.translate('settings', 'manage_crts')}
              .link=${adminEinrichtungSettingsRouter.link('certificates')}
            ></bh-icon-link>
            <bh-icon-link
              icon="contract_edit"
              text=${clientTranslator.translate('settings', 'manage_contracts')}
              .link=${adminEinrichtungSettingsRouter.link('contracts')}
            ></bh-icon-link>
            <bh-icon-link
              icon="mail"
              text=${clientTranslator.translate('settings', 'edit_mail_templates')}
              .link=${adminEinrichtungSettingsRouter.link('mails')}
            ></bh-icon-link>

            <bh-icon-link
              icon="mail"
              text=${clientTranslator.translate('settings', 'embed_form_guide')}
              .link=${adminEinrichtungSettingsRouter.link('embed-form')}
            ></bh-icon-link>

            <bh-icon-link
              icon="palette"
              text=${clientTranslator.translate('settings', 'define_colors')}
              .link=${adminEinrichtungSettingsRouter.link('colors')}
            ></bh-icon-link>

            <bh-icon-link
              icon="category"
              text=${clientTranslator.translate('settings', 'define_product_page')}
              .link=${adminEinrichtungSettingsRouter.link('product-page')}
            ></bh-icon-link>

            <x-hr ${css({ height: '3px' })}></x-hr>

            <bh-icon-link
              icon="description"
              text=${clientTranslator.translate('termsOfUse') + '...'}
              .link=${adminEinrichtungSettingsRouter.link('terms-of-use')}
            ></bh-icon-link>
            <bh-icon-link
              icon="description"
              text="Impressum..."
              .link=${adminEinrichtungSettingsRouter.link('imprint')}
            ></bh-icon-link>
            <bh-icon-link
              icon="description"
              text=${clientTranslator.translate('privacy') + '...'}
              .link=${adminEinrichtungSettingsRouter.link('privacy')}
            ></bh-icon-link>
            <bh-icon-link
              icon="description"
              text=${clientTranslator.translate('accessibility') + '...'}
              .link=${adminEinrichtungSettingsRouter.link('accessibility')}
            ></bh-icon-link>
          </x-flex>
        </x-flex>
      `;
    },
  },
  'general-info': {
    path: '/general-info',
    render: params => html`
      <bh-page-admin-einrichtung-settings-general-info
        institution-id=${params.institutionID}
      ></bh-page-admin-einrichtung-settings-general-info>
    `,
  },
  certificates: {
    path: '/certificates',
    render: params => html`
      <bh-page-admin-einrichtung-settings-certificates
        institution-id=${params.institutionID}
      ></bh-page-admin-einrichtung-settings-certificates>
    `,
  },
  contracts: {
    path: '/contracts',
    render: params => html`
      <bh-page-admin-einrichtung-settings-contracts
        institution-id=${params.institutionID}
      ></bh-page-admin-einrichtung-settings-contracts>
    `,
  },
  mails: {
    path: '/mails',
    render: params => html`
      <bh-page-admin-einrichtung-settings-mails
        institution-id=${params.institutionID}
      ></bh-page-admin-einrichtung-settings-mails>
    `,
  },
  'embed-form': {
    path: '/embed-form',
    render: params => html`
      <bh-page-admin-einrichtung-settings-embedform
        institution-id=${params.institutionID}
      ></bh-page-admin-einrichtung-settings-embedform>
    `,
  },
  'terms-of-use': {
    path: '/terms-of-use',
    render: params => html`
      <bh-page-admin-einrichtung-settings-terms
        institution-id=${params.institutionID}
      ></bh-page-admin-einrichtung-settings-terms>
    `,
  },
  imprint: {
    path: '/imprint',
    render: params => html`
      <bh-page-admin-einrichtung-settings-imprint
        institution-id=${params.institutionID}
      ></bh-page-admin-einrichtung-settings-imprint>
    `,
  },
  privacy: {
    path: '/privacy',
    render: params => html`
      <bh-page-admin-einrichtung-settings-privacy
        institution-id=${params.institutionID}
      ></bh-page-admin-einrichtung-settings-privacy>
    `,
  },
  accessibility: {
    path: '/accessibility',
    render: params => html`
      <bh-page-admin-einrichtung-settings-accessibility
        institution-id=${params.institutionID}
      ></bh-page-admin-einrichtung-settings-accessibility>
    `,
  },
  colors: {
    path: '/colors',
    render: params => html`
      <bh-page-admin-einrichtung-settings-colors
        institution-id=${params.institutionID}
      ></bh-page-admin-einrichtung-settings-colors>
    `,
  },
  'product-page': {
    path: '/product-page',
    metaData: {
      breadcrumbs: ({ translate }) => [
        { content: translate('settings', 'name'), href: '/admin/settings' },
        { content: 'Institutionsübersicht', active: true },
      ],
    },
    render: params =>
      html`
        <bh-page-admin-einrichtung-settings-product-page
          institution-id=${params.institutionID}
        ></bh-page-admin-einrichtung-settings-product-page>
      `,
  },
});

/**
 * @element bh-page-admin-einrichtung-settings
 */
@customElement('bh-page-admin-einrichtung-settings')
export class BHPageAdminEinrichtungSettings extends BuchungsheldLitElement {
  override render() {
    return html` ${watch(adminEinrichtungSettingsRouter.outlet)} `;
  }
}
